<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { GlobeAmericasIcon } from '@heroicons/vue/24/outline';
import { getAlternateLink, getArticleAlternateLink, getEditorialAlternateLink, getGuideCategoryAlternateLink } from '@/plugins/router/localized.ts';
import { OPPOSITE_LOCALE } from '@/plugins/i18n';
import { determineLocale } from '@/utilities';

const { t } = useI18n();
const route = useRoute();

type LinkData = {
  href: string;
  hreflang: string;
  title: string;
  content: string;
};

async function getLink(): Promise<LinkData> {
  const currentLocale = determineLocale();
  const oppositeLocale = OPPOSITE_LOCALE[currentLocale];

  const articleAlternateLink = await getArticleAlternateLink(route);
  if (articleAlternateLink) {
    return {
      ...articleAlternateLink,
      title: t('components.language-switcher.title'),
      content: t('components.language-switcher.content'),
    };
  }

  const guideCategoryAlternateLink = await getGuideCategoryAlternateLink(route);
  if (guideCategoryAlternateLink) {
    return {
      ...guideCategoryAlternateLink,
      title: t('components.language-switcher.title'),
      content: t('components.language-switcher.content'),
    };
  }

  const editorialAlternateLink = await getEditorialAlternateLink(route);
  if (editorialAlternateLink) {
    return {
      ...editorialAlternateLink,
      title: t('components.language-switcher.title'),
      content: t('components.language-switcher.content'),
    };
  }

  const alternateLink = getAlternateLink(route) || { hreflang: oppositeLocale, href: '/' };
  return {
    ...alternateLink,
    title: t('components.language-switcher.title'),
    content: t('components.language-switcher.content'),
  };
}

const link = ref<LinkData | null>(null);

const loadLink = async () => {
  link.value = await getLink();
};

onMounted(loadLink);

watch(
  () => route.fullPath,
  () => {
    loadLink();
  }
);
</script>

<template>
  <div v-if="link" class="no-print translate-x-1.5">
    <a :href="link.href" :hreflang="link.hreflang" :title="link.title" class="focus:outline-ph-green-600 flex rounded-md p-2 text-base/7 font-medium text-gray-700 focus:outline-2 focus:outline-offset-1">
      <GlobeAmericasIcon aria-hidden="true" class="h-7 w-7 rounded-full" />
      <span class="2xs:block hidden pl-1">{{ link.content }}</span>
    </a>
  </div>
</template>

<style lang="css" scoped></style>
